import { JobCard, type JobItem } from "@/components/JobCard";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation as useLocationContext } from "@/contexts/LocationContext";
import { useRegion } from "@/contexts/RegionContext";
import { useZone } from "@/contexts/ZoneContext";
import { calculateDistance } from "@/lib/geolocation";
import { australianCities, regions } from "@shared/schema";
import { useQuery } from "@tanstack/react-query";
import { Briefcase, DollarSign, MapPin } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "wouter";

// Phase 3.4: 구인/구직 카테고리
const jobTypes = [
  { id: "all", label: "전체" },
  { id: "casual", label: "캐주얼/파트타임" },
  { id: "professional", label: "전문직" },
  { id: "student", label: "학생알바" },
  { id: "working-holiday", label: "워홀" },
];

// Phase 3.4: 근무형태
const workTypes = [
  { id: "all", label: "전체" },
  { id: "full-time", label: "정규직" },
  { id: "part-time", label: "파트타임" },
  { id: "casual", label: "캐주얼" },
  { id: "contract", label: "계약직" },
];

export default function Jobs() {
  const { user } = useAuth();
  const { selectedRegion, selectedSuburb } = useRegion();
  const { selectedZoneId } = useZone();
  const { userLocation } = useLocationContext();
  const [selectedState, setSelectedState] = useState<string>(
    selectedRegion || "ALL"
  );
  const [selectedCity, setSelectedCity] = useState<string>("ALL");
  const [searchTerm, setSearchTerm] = useState<string>("");
  // Phase 3.4: 구인/구직 전용 필터
  const [selectedJobType, setSelectedJobType] = useState<string>("all");
  const [selectedWorkType, setSelectedWorkType] = useState<string>("all");
  const [minWage, setMinWage] = useState<string>("");
  const [maxWage, setMaxWage] = useState<string>("");
  const [industry, setIndustry] = useState<string>("");

  // Phase 3.4: 구인/구직 목록 조회 (category: 'jobs' 필터)
  const { data: listings, isLoading } = useQuery<JobItem[]>({
    queryKey: ["/api/unified-market", selectedZoneId, selectedState, selectedCity, "jobs"],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append("category", "jobs");
      params.append("type", "ad");
      params.append("zone", selectedZoneId);
      if (selectedState !== "ALL") params.append("state", selectedState);
      if (selectedCity !== "ALL") params.append("city", selectedCity);

      const res = await fetch(`/api/unified-market?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to fetch listings");
      const data = await res.json();
      // 응답이 { ok: true, data: { items: [...] } } 형식일 수 있음
      if (data?.ok && data?.data?.items) {
        return data.data.items;
      }
      if (Array.isArray(data)) {
        return data;
      }
      return [];
    },
  });

  // Phase 3.4: 거리 계산 및 고급 필터링 로직
  const filteredListings = useMemo(() => {
    if (!listings) return [];

    // 거리 계산
    let result = listings.map((listing) => {
      let distance: number | undefined = undefined;
      if (
        userLocation &&
        listing.latitude &&
        listing.longitude &&
        typeof listing.latitude === "number" &&
        typeof listing.longitude === "number"
      ) {
        distance = calculateDistance(
          userLocation.latitude,
          userLocation.longitude,
          listing.latitude,
          listing.longitude
        );
      }
      return { ...listing, distance };
    });

    // 필터링
    result = result.filter((listing) => {
      // 검색어 필터
      if (
        searchTerm &&
        !listing.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !listing.description
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) &&
        !listing.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
      ) {
        return false;
      }

      // 구인/구직 타입 필터
      if (selectedJobType !== "all" && listing.jobType !== selectedJobType) {
        return false;
      }

      // 근무형태 필터
      if (selectedWorkType !== "all" && listing.workType !== selectedWorkType) {
        return false;
      }

      // 시급 필터
      if (minWage || maxWage) {
        const wage = listing.priceCents;
        if (wage === null || wage === undefined || wage === -1 || wage === 0) {
          // 시급협의는 필터에서 제외하지 않음
        } else {
          const wageInDollars = wage / 100;
          if (minWage && wageInDollars < parseFloat(minWage)) return false;
          if (maxWage && wageInDollars > parseFloat(maxWage)) return false;
        }
      }

      // 업종 필터
      if (
        industry &&
        listing.industry &&
        !listing.industry.toLowerCase().includes(industry.toLowerCase())
      ) {
        return false;
      }

      return true;
    });

    // 거리순 정렬 (거리가 있는 경우)
    return result.sort((a, b) => {
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      if (a.distance !== undefined) return -1;
      if (b.distance !== undefined) return 1;
      // 최신순 정렬
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [
    listings,
    searchTerm,
    selectedJobType,
    selectedWorkType,
    minWage,
    maxWage,
    industry,
    userLocation,
  ]);

  const stateRegion = regions.find((r) => r.id === selectedState);
  const availableCities =
    selectedState !== "ALL" && selectedState in australianCities
      ? australianCities[selectedState as keyof typeof australianCities]
      : [];

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <div className="bg-primary/5 border-b">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl font-bold mb-3" data-testid="text-title">
              구인/구직
            </h1>
            <p className="text-lg text-muted-foreground mb-6">
              캐주얼/파트타임, 전문직, 학생알바, 워홀 - 호주 한인 커뮤니티
              구인구직 정보
            </p>
            <div className="flex gap-4 justify-center items-center flex-wrap">
              <Badge variant="secondary" className="gap-1">
                <Briefcase className="w-3 h-3" />
                구인구직
              </Badge>
              <Badge variant="secondary" className="gap-1">
                <MapPin className="w-3 h-3" />
                지역기반
              </Badge>
              <Badge variant="secondary" className="gap-1">
                <DollarSign className="w-3 h-3" />
                시급 표시
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar - Filters */}
          <aside className="lg:col-span-1">
            <Card>
              <CardContent className="p-4 space-y-4">
                {/* Search */}
                <div>
                  <label className="text-sm font-medium mb-2 block">검색</label>
                  <Input
                    placeholder="제목, 회사명 또는 설명 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    data-testid="input-search"
                  />
                </div>

                {/* State Filter */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    주/지역
                  </label>
                  <Select
                    value={selectedState}
                    onValueChange={(value) => {
                      setSelectedState(value);
                      setSelectedCity("ALL");
                    }}
                  >
                    <SelectTrigger data-testid="select-state">
                      <SelectValue placeholder="주 선택" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">전체 지역</SelectItem>
                      {regions
                        .filter((r) => r.id !== "ALL")
                        .map((region) => (
                          <SelectItem key={region.id} value={region.id}>
                            {region.label}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* City Filter */}
                {availableCities.length > 0 && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      도시
                    </label>
                    <Select
                      value={selectedCity}
                      onValueChange={setSelectedCity}
                    >
                      <SelectTrigger data-testid="select-city">
                        <SelectValue placeholder="도시 선택" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ALL">전체 도시</SelectItem>
                        {availableCities.map((city) => (
                          <SelectItem key={city.id} value={city.id}>
                            {city.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Phase 3.4: 구인/구직 타입 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">타입</label>
                  <Select
                    value={selectedJobType}
                    onValueChange={setSelectedJobType}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {jobTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Phase 3.4: 근무형태 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    근무형태
                  </label>
                  <Select
                    value={selectedWorkType}
                    onValueChange={setSelectedWorkType}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {workTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Phase 3.4: 시급 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">
                    시급 범위
                  </label>
                  <div className="flex gap-2">
                    <Input
                      type="number"
                      placeholder="최소"
                      value={minWage}
                      onChange={(e) => setMinWage(e.target.value)}
                      className="flex-1"
                    />
                    <Input
                      type="number"
                      placeholder="최대"
                      value={maxWage}
                      onChange={(e) => setMaxWage(e.target.value)}
                      className="flex-1"
                    />
                  </div>
                </div>

                {/* Phase 3.4: 업종 필터 */}
                <div>
                  <label className="text-sm font-medium mb-2 block">업종</label>
                  <Input
                    placeholder="업종 검색..."
                    value={industry}
                    onChange={(e) => setIndustry(e.target.value)}
                  />
                </div>

                {/* Post Button */}
                {user && (
                  <Link href="/jobs/register">
                    <Button className="w-full" data-testid="button-register">
                      구인/구직 등록
                    </Button>
                  </Link>
                )}
                {!user && (
                  <Button className="w-full" variant="outline" disabled>
                    로그인 후 등록 가능
                  </Button>
                )}
              </CardContent>
            </Card>
          </aside>

          {/* Main Content - Listings */}
          <div className="lg:col-span-3">
            {/* Results Count */}
            <div className="mb-4 flex justify-between items-center">
              <p
                className="text-sm text-muted-foreground"
                data-testid="text-count"
              >
                {filteredListings
                  ? `${filteredListings.length}개의 구인/구직`
                  : "로딩 중..."}
              </p>
            </div>

            {/* Listings Grid */}
            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {[...Array(6)].map((_, i) => (
                  <Card key={i}>
                    <Skeleton className="h-48 w-full" />
                    <CardContent className="p-4">
                      <Skeleton className="h-4 w-3/4 mb-2" />
                      <Skeleton className="h-4 w-1/2" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : filteredListings && filteredListings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredListings.map((listing) => (
                  <JobCard
                    key={listing.id}
                    item={listing}
                    onInquire={() => {
                      // TODO: 채팅 시작 로직
                      window.location.href = `/chat-start/jobs/${listing.id}`;
                    }}
                  />
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-16 text-center">
                  <Briefcase className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
                  <p className="text-lg font-medium mb-2">
                    등록된 구인/구직이 없습니다
                  </p>
                  <p className="text-sm text-muted-foreground mb-6">
                    첫 번째 구인/구직을 등록해보세요!
                  </p>
                  {user && (
                    <Link href="/jobs/register">
                      <Button>구인/구직 등록하기</Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
